// Connector metadata — ported from Pulse_Base for the Architecture demo.
// Brand logos load from src/assets/connector-icons/ when present; falls
// back to the lucide icon listed on each entry.
import {
  Calculator,
  Users,
  Calendar,
  BarChart3,
  Search,
  Briefcase,
  CheckSquare,
} from 'lucide-react';

const logos = {};
function tryLoad(name) {
  // Try SVG then PNG. Missing files just fall through to the lucide icon.
  for (const ext of ['svg', 'png']) {
    try { return require(`../assets/connector-icons/${name}.${ext}`); } catch {}
  }
  return null;
}
logos.quickbooks = tryLoad('quickbooks');
logos.active_campaign = tryLoad('active_campaign');
logos.calendly = tryLoad('calendly');
logos.hubspot = tryLoad('hubspot');
logos.google_analytics = tryLoad('google_analytics');
logos.google_search_console = tryLoad('google_search_console');
logos.grain = tryLoad('grain');
logos.zoom = tryLoad('zoom');
logos.clickup = tryLoad('clickup');
logos.semrush = tryLoad('semrush');

const CONNECTOR_META = {
  quickbooks: {
    label: 'QuickBooks',
    icon: Calculator,
    logoSrc: logos.quickbooks,
    domain: 'Finance',
    color: 'bg-green-100 text-green-700',
    authType: 'oauth',
    description: 'Financial data: invoices, payments, accounts',
    needsTransform: false,
  },
  active_campaign: {
    label: 'ActiveCampaign',
    icon: Users,
    logoSrc: logos.active_campaign,
    domain: 'Marketing',
    color: 'bg-blue-100 text-blue-700',
    authType: 'api_key',
    credentialFields: [
      { key: 'api_key', label: 'API Key', type: 'password' },
      { key: 'base_url', label: 'Base URL', type: 'text', placeholder: 'https://youraccountname.api-us1.com' },
    ],
    description: 'Contacts, email campaigns, tags',
    needsTransform: true,
  },
  calendly: {
    label: 'Calendly',
    icon: Calendar,
    logoSrc: logos.calendly,
    domain: 'Sales',
    color: 'bg-purple-100 text-purple-700',
    authType: 'api_key',
    credentialFields: [
      { key: 'api_key', label: 'Personal Access Token', type: 'password' },
    ],
    description: 'Booked calls, event types',
    needsTransform: true,
  },
  hubspot: {
    label: 'HubSpot',
    icon: Briefcase,
    logoSrc: logos.hubspot,
    domain: 'Sales',
    color: 'bg-orange-100 text-orange-700',
    authType: 'api_key',
    credentialFields: [
      { key: 'api_key', label: 'Private App Token (pat-*)', type: 'password' },
    ],
    description: 'Deals, contacts, pipeline',
    needsTransform: true,
  },
  google_analytics: {
    label: 'Google Analytics',
    icon: BarChart3,
    logoSrc: logos.google_analytics,
    domain: 'Marketing',
    color: 'bg-yellow-100 text-yellow-700',
    authType: 'api_key',
    credentialFields: [
      { key: 'service_account_json', label: 'Service Account JSON', type: 'textarea' },
      { key: 'property_id', label: 'GA4 Property ID', type: 'text', placeholder: 'properties/316919056' },
    ],
    description: 'Sessions, traffic sources, engagement',
    needsTransform: true,
  },
  google_search_console: {
    label: 'Search Console',
    icon: Search,
    logoSrc: logos.google_search_console,
    domain: 'Marketing',
    color: 'bg-indigo-100 text-indigo-700',
    authType: 'api_key',
    credentialFields: [
      { key: 'service_account_json', label: 'Service Account JSON (shared with GA4)', type: 'textarea' },
      { key: 'site_url', label: 'Site URL', type: 'text', placeholder: 'sc-domain:scaletime.co' },
    ],
    description: 'Search impressions, queries, pages',
    needsTransform: true,
  },
  grain: {
    label: 'Grain',
    icon: BarChart3,
    logoSrc: logos.grain,
    domain: 'Sales',
    color: 'bg-emerald-100 text-emerald-700',
    authType: 'api_key',
    credentialFields: [
      { key: 'api_token', label: 'Grain API Token', type: 'password' },
      { key: 'api_version', label: 'API Version', type: 'text', placeholder: '2025-10-31' },
    ],
    description: 'Meeting recordings, show-up tracking',
    needsTransform: false,
  },
  zoom: {
    label: 'Zoom',
    icon: Calendar,
    logoSrc: logos.zoom,
    domain: 'Sales',
    color: 'bg-blue-100 text-blue-700',
    authType: 'service_account',
    credentialFields: [
      { key: 'account_id', label: 'Zoom Account ID', type: 'text' },
      { key: 'client_id', label: 'Zoom Client ID', type: 'text' },
      { key: 'client_secret', label: 'Zoom Client Secret', type: 'password' },
    ],
    description: 'Meeting attendance, show-up tracking',
    needsTransform: false,
  },
  clickup: {
    label: 'ClickUp',
    icon: CheckSquare,
    logoSrc: logos.clickup,
    domain: 'Operations',
    color: 'bg-pink-100 text-pink-700',
    authType: 'api_key',
    credentialFields: [
      { key: 'api_key', label: 'API Key', type: 'password' },
    ],
    description: 'Tasks, projects, time tracking',
    needsTransform: false,
  },
  semrush: {
    label: 'Semrush',
    icon: BarChart3,
    logoSrc: logos.semrush,
    domain: 'Marketing',
    color: 'bg-orange-100 text-orange-700',
    authType: 'api_key',
    credentialFields: [
      { key: 'api_key', label: 'Semrush API Key', type: 'password' },
      { key: 'domains', label: 'Domains (comma-separated, e.g. scaletime.co)', type: 'text' },
      { key: 'database', label: 'Database region (default: us)', type: 'text', placeholder: 'us' },
    ],
    description: 'Authority Score, organic traffic, keyword ranks',
    needsTransform: true,
  },
};

export const getConnectorMeta = (connectorType) =>
  CONNECTOR_META[connectorType] || {
    label: connectorType,
    icon: Briefcase,
    domain: 'Other',
    color: 'bg-gray-100 text-gray-700',
    authType: 'api_key',
    description: '',
  };

export default CONNECTOR_META;
